import { makeAutoObservable, runInAction } from 'mobx';
import { sessionsApi } from '@/api/sessions';
import { isAbortError, RequestSequencer } from '@/api/requests';
import { normalizeApiError, type ApiError } from '@/lib/errors';
import type { AuthScopeChange } from '@/stores/AuthStore';
import type { DaySession, DaySessionListRow, DaySessionSummary } from '@/types/sessions';

/**
 * SessionStore (V1-06) — day-session (shift) server state for the POS shell.
 *
 * Wire facts this store is built on (docs/05a-api-baseline.md §7, re-verified
 * against pos-api DaySessionController/DaySession.php at HEAD 3ac0f6b):
 * - Cash-enabled sessions require a physical opening float and manager-reviewed
 *   blind count. Legacy sessions retain their original lifecycle.
 * - `GET /day-sessions/current?terminal_id=` is always 200; data:null means
 *   "day not started" — not an error.
 * - `POST /day-sessions/start` 400 "already open" returns the EXISTING
 *   session; sessionsApi.start adopts it (adopted:true) instead of throwing.
 * - `GET /day-sessions` history 500s when X-Branch is sent (B1) —
 *   sessionsApi.list isolates the skipBranchHeader workaround and this store
 *   always passes the validated `branch_id` query param instead.
 *
 * Scope: a day session belongs to ONE terminal. Current-session state is
 * keyed by the branch/terminal scope; any organization/branch/terminal change
 * or logout must reset the store (lead wires `onScopeChange` into
 * AuthStore.registerScopeListener at integration).
 */

export type SessionRequestStatus = 'idle' | 'loading' | 'loaded' | 'error';

/** Narrow structural view of AuthStore — the lead passes RootStore.auth. */
export interface SessionScopeSource {
  readonly branch: { id: number; name: string } | null;
  readonly terminal: { id: number; name: string } | null;
}

export interface StartDayResult {
  ok: boolean;
  /** True when an already-open session was adopted instead of created. */
  adopted: boolean;
}

export interface SessionHistoryParams {
  page?: number;
  per_page?: number;
  from_date?: string;
  to_date?: string;
  terminal_id?: number;
  status?: 'open' | 'closed';
}

/** Local calendar date (YYYY-MM-DD) for the validated history date filters. */
export function localDateString(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** "5h 23m" — mirrors the server's own duration formatting. */
export function formatDuration(startedAtIso: string, endMs: number): string {
  const start = new Date(startedAtIso).getTime();
  if (!Number.isFinite(start)) return '—';
  const totalMinutes = Math.max(0, Math.floor((endMs - start) / 60000));
  return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
}

export class SessionStore {
  /** Current (or just-closed) session for the selected terminal. */
  currentSession: DaySession | null = null;
  currentStatus: SessionRequestStatus = 'idle';
  currentError: ApiError | null = null;

  /** One in-flight start/end mutation at a time. */
  isMutating = false;
  mutationError: ApiError | null = null;

  /** Z-report (GET /day-sessions/{id}/summary). */
  summary: DaySessionSummary | null = null;
  summaryStatus: SessionRequestStatus = 'idle';
  summaryError: ApiError | null = null;

  /** History slice via sessionsApi.list (B1 workaround inside the adapter). */
  history: DaySessionListRow[] = [];
  historyStatus: SessionRequestStatus = 'idle';
  historyError: ApiError | null = null;
  historyTotal = 0;
  historyPage = 1;
  historyLastPage = 1;

  /** Observable clock for the `duration` computed; UI ticks it via refreshNow(). */
  nowMs: number = Date.now();

  /** Scope key the loaded currentSession belongs to (stale-scope guard). */
  private loadedScopeKey: string | null = null;
  private seq = new RequestSequencer();

  constructor(private scopeSource: SessionScopeSource) {
    makeAutoObservable<SessionStore, 'scopeSource' | 'seq' | 'loadedScopeKey'>(
      this,
      {
        scopeSource: false,
        seq: false,
        loadedScopeKey: false,
      },
      // autoBind so actions can be handed to hooks/handlers directly
      { autoBind: true },
    );
  }

  // ── computeds ────────────────────────────────────────────────────────────

  /** branch:terminal identity of the selected scope. */
  get scopeKey(): string {
    const { branch, terminal } = this.scopeSource;
    return `${branch?.id ?? 'none'}:${terminal?.id ?? 'none'}`;
  }

  get isDayOpen(): boolean {
    return this.currentSession?.status === 'open';
  }

  get canTrade(): boolean {
    return this.isDayOpen && (this.currentSession?.cash_state == null || this.currentSession.cash_state === 'trading');
  }

  /** Formatted "5h 23m" since start of the OPEN session (refreshNow() ticks it). */
  get duration(): string | null {
    if (!this.currentSession || this.currentSession.status !== 'open') return null;
    return formatDuration(this.currentSession.started_at, this.nowMs);
  }

  /** True once the current-session check has resolved for this scope. */
  get isResolved(): boolean {
    return this.currentStatus === 'loaded';
  }

  // ── actions ──────────────────────────────────────────────────────────────

  refreshNow(nowMs: number = Date.now()): void {
    this.nowMs = nowMs;
  }

  clearMutationError(): void {
    this.mutationError = null;
  }

  /**
   * Resolve the current session for the selected terminal. Retains a loaded
   * OPEN session while refreshing the SAME scope (retained-refresh); a scope
   * change or a failed re-resolution clears it — a stale session from another
   * terminal must never survive (its id would be stamped onto orders).
   */
  async loadCurrent(): Promise<void> {
    const terminal = this.scopeSource.terminal;
    if (!terminal) {
      this.currentSession = null;
      this.currentStatus = 'idle';
      this.currentError = null;
      this.loadedScopeKey = null;
      return;
    }
    const scopeKey = this.scopeKey;
    if (this.loadedScopeKey !== scopeKey) this.currentSession = null;
    this.currentStatus = 'loading';
    this.currentError = null;

    const ticket = this.seq.begin('current');
    try {
      const session = await sessionsApi.getCurrent(terminal.id, { signal: ticket.signal });
      if (!ticket.isCurrent()) return;
      runInAction(() => {
        if (this.scopeKey !== scopeKey) return; // scope moved mid-flight
        this.currentSession = session;
        this.currentStatus = 'loaded';
        this.loadedScopeKey = scopeKey;
      });
    } catch (e) {
      if (isAbortError(e) || !ticket.isCurrent()) return;
      runInAction(() => {
        this.currentSession = null; // unresolved state must block tender
        this.currentStatus = 'error';
        this.currentError = normalizeApiError(e);
        this.loadedScopeKey = null;
      });
    }
  }

  /**
   * Open the day on the selected terminal. Notes only — the wire has no
   * opening-cash field. A 400 already-open response is ADOPTED (the existing
   * session comes back in `data`; sessionsApi handles the shape).
   */
  async start(notes?: string, openingCash?: string, operationId?: string): Promise<StartDayResult> {
    const { terminal, branch } = this.scopeSource;
    if (!terminal || this.isMutating) return { ok: false, adopted: false };
    const scopeKey = this.scopeKey;
    this.isMutating = true;
    this.mutationError = null;
    try {
      const trimmed = notes?.trim();
      const result = await sessionsApi.start({
        terminal_id: terminal.id,
        ...(openingCash === undefined ? {} : { opening_cash: openingCash, operation_id: operationId }),
        ...(branch ? { branch_id: branch.id } : {}),
        ...(trimmed ? { notes: trimmed } : {}),
      });
      runInAction(() => {
        if (this.scopeKey === scopeKey) {
          this.currentSession = result.session;
          this.currentStatus = 'loaded';
          this.currentError = null;
          this.loadedScopeKey = scopeKey;
        }
      });
      return { ok: true, adopted: result.adopted };
    } catch (e) {
      runInAction(() => {
        this.mutationError = normalizeApiError(e);
      });
      return { ok: false, adopted: false };
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  /**
   * Close the open day session. Keeps the CLOSED session in `currentSession`
   * (isDayOpen turns false) so the just-closed Z-report stays reachable until
   * the next resolution or day open.
   */
  async end(notes?: string): Promise<boolean> {
    const open = this.currentSession;
    if (!open || open.status !== 'open' || this.isMutating) return false;
    const scopeKey = this.scopeKey;
    this.isMutating = true;
    this.mutationError = null;
    try {
      const trimmed = notes?.trim();
      const closed = await sessionsApi.end(open.id, trimmed ? trimmed : undefined);
      runInAction(() => {
        if (this.scopeKey === scopeKey) {
          this.currentSession = closed;
          this.currentStatus = 'loaded';
        }
      });
      return true;
    } catch (e) {
      runInAction(() => {
        this.mutationError = normalizeApiError(e);
      });
      return false;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  /** Load the Z-report for a session (numbers arrive as plain JSON numbers). */
  async loadSummary(sessionId: number): Promise<void> {
    this.summaryStatus = 'loading';
    this.summaryError = null;
    this.summary = null;
    const ticket = this.seq.begin('summary');
    try {
      const summary = await sessionsApi.summary(sessionId, { signal: ticket.signal });
      if (!ticket.isCurrent()) return;
      runInAction(() => {
        this.summary = summary;
        this.summaryStatus = 'loaded';
      });
    } catch (e) {
      if (isAbortError(e) || !ticket.isCurrent()) return;
      runInAction(() => {
        this.summaryStatus = 'error';
        this.summaryError = normalizeApiError(e);
      });
    }
  }

  /**
   * History slice. Branch filtering goes through the VALIDATED `branch_id`
   * query param — sessionsApi.list omits the X-Branch header (B1 workaround,
   * docs/05a-api-baseline.md §7.5); this store never bypasses that adapter.
   */
  async loadHistory(params: SessionHistoryParams = {}): Promise<void> {
    const branch = this.scopeSource.branch;
    this.historyStatus = 'loading';
    this.historyError = null;
    const ticket = this.seq.begin('history');
    try {
      const result = await sessionsApi.list(
        { ...(branch ? { branch_id: branch.id } : {}), ...params },
        { signal: ticket.signal },
      );
      if (!ticket.isCurrent()) return;
      runInAction(() => {
        this.history = result.sessions;
        this.historyTotal = result.total;
        this.historyPage = result.currentPage;
        this.historyLastPage = result.lastPage;
        this.historyStatus = 'loaded';
      });
    } catch (e) {
      if (isAbortError(e) || !ticket.isCurrent()) return;
      runInAction(() => {
        this.historyStatus = 'error';
        this.historyError = normalizeApiError(e);
      });
    }
  }

  /** Today's sessions on the selected terminal (Today screen; history = office). */
  async loadTodayHistory(): Promise<void> {
    const terminal = this.scopeSource.terminal;
    const today = localDateString();
    await this.loadHistory({
      from_date: today,
      to_date: today,
      ...(terminal ? { terminal_id: terminal.id } : {}),
      per_page: 25,
    });
  }

  /** Full reset — scope change/logout seam. Aborts in-flight requests. */
  reset(): void {
    this.seq.abortAll();
    this.currentSession = null;
    this.currentStatus = 'idle';
    this.currentError = null;
    this.isMutating = false;
    this.mutationError = null;
    this.summary = null;
    this.summaryStatus = 'idle';
    this.summaryError = null;
    this.history = [];
    this.historyStatus = 'idle';
    this.historyError = null;
    this.historyTotal = 0;
    this.historyPage = 1;
    this.historyLastPage = 1;
    this.loadedScopeKey = null;
  }

  /**
   * Scope listener the lead registers on AuthStore. Session state is
   * terminal-scoped, so EVERY change kind (organization ⊃ branch ⊃ terminal,
   * plus logout) invalidates it.
   */
  onScopeChange(_change: AuthScopeChange): void {
    this.reset();
  }
}
